import { useRouter, useRouterState } from "@tanstack/react-router";
import { useTolgee } from "@tolgee/react";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { markLocaleSwitchedAfterHydration, readLocale } from "../../lib/locale";
import { initializeLocaleAtom } from "../../stores/locale-store";

/**
 * URL 프리픽스 로케일은 __root beforeLoad가 서버/클라이언트 공통으로 적용한다. 이 이펙트는
 * 그 위에 브라우저 전용 신호(저장된 선호 → navigator.language)를 마운트 후 얹는다:
 * 서버는 알 수 없는 값이라 첫 렌더는 URL 로케일(en)로 그린 뒤 여기서 전환한다(짧은 플래시).
 * 전환 시 router.invalidate()로 beforeLoad/head()를 다시 돌려 title·meta도 따라오게 한다.
 */
export function LocaleSync() {
	const tolgee = useTolgee();
	const router = useRouter();
	const initializeLocale = useSetAtom(initializeLocaleAtom);
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	// 판정은 반드시 라우터 상태 pathname으로 — SPA navigate 중 window.location이
	// 늦게 커밋되는 구간에서 이전 URL의 locale로 되돌리는 레이스가 있었다.
	// 내비게이션마다 한 번만(deps=pathname): /ko 페이지에서 zh로 JS 전환한 상태를
	// 다음 내비게이션 전까지 되돌리지 않는다(기존 동작 유지).
	useEffect(() => {
		const locale = readLocale(pathname);
		if (locale === tolgee.getLanguage()) return;
		// idle 타임으로 미룬다: React는 Suspense 경계(코드 스플릿 라우트)를 루트 커밋 뒤
		// 별도 태스크에서 hydrate한다 — 그 전에 언어를 바꾸면 경계가 SSR HTML(URL 로케일)과
		// 다른 텍스트로 hydrate돼 mismatch → 클라이언트 재렌더로 복구된다. 대부분은 idle
		// 시점에 hydration이 끝나 있어 일반 재렌더로 끝난다; 늦은 lazy 청크는 복구 경로
		// (client.tsx onRecoverableError가 예상 케이스로 처리).
		const schedule: (cb: () => void) => number =
			typeof window.requestIdleCallback === "function"
				? (cb) => window.requestIdleCallback(cb, { timeout: 500 })
				: (cb) => window.setTimeout(cb, 0);
		const cancel =
			typeof window.cancelIdleCallback === "function"
				? window.cancelIdleCallback
				: window.clearTimeout;
		const handle = schedule(() => {
			markLocaleSwitchedAfterHydration();
			initializeLocale(locale);
			tolgee.changeLanguage(locale);
			router.invalidate();
		});
		return () => cancel(handle);
	}, [pathname, initializeLocale, tolgee, router]);

	return null;
}
